"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { Loader, Mail, Phone, MapPin } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactBooking() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="contact-booking" ref={ref} className={`min-h-screen bg-muted-light-background py-16 px-4 transition-opacity duration-[var(--duration-medium)] ${isVisible ? "animate-fade-in-up" : "opacity-0"}`}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 text-center">
          <h2 className="font-display text-4xl font-bold text-foreground-light mb-3">
            Contact &amp; Booking
          </h2>
          <p className="text-muted-foreground text-lg">
            Let&apos;s make your special day come to life exactly as it deserves
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr,300px]">
          <FormProvider className="card bg-light-background p-8 transition-all duration-[var(--duration-medium)] focus-within:shadow-strong focus-within:translate-y-[-3px]" formId="contact_booking_form_home" sectionName="contact-booking" data-recipient-email="denverweddingco@gmail.com" defaultValues={{
          name: "",
          email: "",
          phone: "",
          event_date: "",
          event_type: "",
          venue_location: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          event_date: {
            required: true
          },
          event_type: {
            required: true
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 1000
          }
        }} submitText="Book Your Event" successMessage="Thank you! We'll get back to you soon to discuss your special day." errorMessage="Something went wrong. Please try again or contact us directly.">
            <ContactBookingForm />
          </FormProvider>

          <aside className="hidden lg:block">
            <div className="card sticky top-8 bg-light-background p-6 space-y-6">
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground-light mb-4">
                  Get in Touch
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="size-5 text-primary-dark mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground-light">
                        Email
                      </p>
                      <Link to="mailto:denverweddingco@gmail.com" className="text-sm text-muted-foreground hover:text-primary-dark transition-colors">
                        denverweddingco@gmail.com
                      </Link>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Phone className="size-5 text-primary-dark mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground-light">
                        Phone
                      </p>
                      <Link to="tel:+17202894986" className="text-sm text-muted-foreground hover:text-primary-dark transition-colors">
                        (720) 289-4986
                      </Link>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <MapPin className="size-5 text-primary-dark mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground-light">
                        Location
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Denver, CO
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Available by appointment
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}
function ContactBookingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary-light border border-primary rounded-lg text-center">
        <div className="mb-4">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-primary text-primary-foreground mb-4">
            <svg className="size-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        <h3 className="font-display text-xl font-semibold text-foreground-light mb-2">
          Booking Request Received!
        </h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="hover-lift">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          <FormField control={control} name="name" rules={{
          required: "Name is required",
          minLength: {
            value: 2,
            message: "Name must be at least 2 characters"
          },
          maxLength: {
            value: 100,
            message: "Name must be less than 100 characters"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Your Name
                </FormLabel>
                <FormControl>
                  <Input placeholder="Jane Smith" className="focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormDescription>First and last name</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jane@example.com" className="focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <FormField control={control} name="phone" rules={{
          required: "Phone number is required",
          pattern: {
            value: /^[\d\s\-\(\)\+]+$/,
            message: "Please enter a valid phone number"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Phone Number
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="(303) 555-1234" className="focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="event_date" rules={{
          required: "Event date is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Event Date
                </FormLabel>
                <FormControl>
                  <Input type="date" className="focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormDescription>Select the date of your wedding or event</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <FormField control={control} name="event_type" rules={{
          required: "Event type is required"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Event Type
                </FormLabel>
                <FormControl>
                  <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus:shadow-medium disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                    <option value="">Select an event type</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Reception">Reception</option>
                    <option value="Ceremony">Ceremony</option>
                    <option value="Party">Party</option>
                    <option value="Corporate Event">Corporate Event</option>
                    <option value="Other">Other</option>
                  </select>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="venue_location" render={({
          field
        }) => <FormItem>
                <FormLabel className="transition-all duration-[var(--duration-fast)]">
                  Venue / Location
                </FormLabel>
                <FormControl>
                  <Input placeholder="Denver, CO or specific venue name" className="focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must be less than 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="transition-all duration-[var(--duration-fast)]">
                Additional Details / Message
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your vision, special requests, or any questions..." className="min-h-32 focus:shadow-medium transition-all duration-[var(--duration-medium)]" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{submitError}</p>
          </div>}

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSubmitting} className="btn-primary min-w-[200px] relative overflow-hidden">
            {isSubmitting ? <>
                <Loader className="animate-spin size-4" />
                Sending...
              </> : "Book Your Event"}
          </Button>
        </div>
      </div>
    </Form>;
}